import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Container,
  Avatar,
  Card,
  CardContent,
  Stack,
  Fade,
  Alert,
  Button,
  Chip,
  Badge,
  CircularProgress,
} from '@mui/material';
import { 
  Send as SendIcon, 
  Person as PersonIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  Dashboard as DashboardIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import Markdown from 'markdown-to-jsx';
import { useChat, ChatMessage } from '../hooks/useChat';
import { useCaseState } from '../hooks/useCaseState';
import DiseaseModal from './DiseaseModal';
import SourcesModal from './SourcesModal';
import InlineSourceBadges from './InlineSourceBadges';
import InlineWebSourceBadges from './InlineWebSourceBadges';
import MessageSourceBadges from './MessageSourceBadges';
import type { WebSourceReference } from '../hooks/useChat';
import ConversationDashboardModal from './ConversationDashboardModal';
import CaseDialog from './CaseDialog';
import CaseButton from './CaseButton';
import { parseURN } from '../utils/urnParser';
import { gekkoApi } from '../utils/gekkoApi';
import { copilotApi, CopilotApiError } from '../services/copilotApi';
import type { DocumentSummary, Document, DocumentVersionInfo, DocumentCreatedEvent, DocumentRevisedEvent } from '../types/documents';
import { useFlashMessages } from '../hooks/useFlashMessages';
import { useWebSocket } from '../contexts/WebSocketContext';

// Context that exposes the currently-rendered copilot message's web sources
// to descendants of the Markdown renderer (so [Web:N] markers can be resolved
// against the inlined per-message webSources array).
const MessageWebSourcesContext = React.createContext<WebSourceReference[]>([]);

// Image resizing constants for vision API compatibility
const MAX_IMAGE_DIMENSION = 2048; // Max width or height in pixels
const MAX_IMAGE_FILE_SIZE = 3 * 1024 * 1024; // 3MB (safely under Bedrock's 3.75MB limit)
const JPEG_QUALITY = 0.85; // Good balance between quality and file size
const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * Load an image file into an HTMLImageElement
 */
const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Calculate new dimensions maintaining aspect ratio
 */
const calculateDimensions = (
  img: HTMLImageElement,
  maxDimension: number
): { width: number; height: number } => {
  let { width, height } = img;

  if (width <= maxDimension && height <= maxDimension) {
    return { width, height };
  }

  if (width > height) {
    height = Math.round((height / width) * maxDimension);
    width = maxDimension;
  } else {
    width = Math.round((width / height) * maxDimension);
    height = maxDimension;
  }

  return { width, height };
};

/**
 * Resize an image file to fit within size limits for vision API
 * Returns the original file if no resizing is needed or if not an image
 */
const resizeImageForUpload = async (file: File): Promise<File> => {
  // Skip non-image files
  if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
    return file;
  }

  // Load the image to check dimensions
  const img = await loadImage(file);
  const needsResize = img.width > MAX_IMAGE_DIMENSION || img.height > MAX_IMAGE_DIMENSION;
  const needsCompression = file.size > MAX_IMAGE_FILE_SIZE;

  // If already within limits, return original
  if (!needsResize && !needsCompression) {
    URL.revokeObjectURL(img.src); // Clean up object URL
    return file;
  }

  // Calculate new dimensions
  const { width, height } = calculateDimensions(img, MAX_IMAGE_DIMENSION);

  // Create canvas and resize
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    URL.revokeObjectURL(img.src);
    console.warn('Failed to get canvas context, returning original file');
    return file;
  }

  // Draw image with high quality scaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  // Clean up object URL
  URL.revokeObjectURL(img.src);

  // Convert to blob - use JPEG for better compression unless it's a GIF (preserve animation)
  // or PNG with transparency (check for alpha channel)
  const outputType = file.type === 'image/gif' ? 'image/gif' : 'image/jpeg';
  const quality = outputType === 'image/jpeg' ? JPEG_QUALITY : undefined;

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, outputType, quality);
  });

  if (!blob) {
    console.warn('Failed to create blob, returning original file');
    return file;
  }

  // Create new file with updated name if format changed
  const newFileName = outputType === 'image/jpeg' && file.type !== 'image/jpeg'
    ? file.name.replace(/\.[^.]+$/, '.jpg')
    : file.name;

  const resizedFile = new File([blob], newFileName, { type: outputType });

  console.log(`Image resized: ${file.name} (${file.size} bytes, ${img.width}x${img.height}) -> ${resizedFile.name} (${resizedFile.size} bytes, ${width}x${height})`);

  return resizedFile;
};

export interface ChatInterfaceRef {
  clearMessages: () => Promise<void>;
}

interface ChatInterfaceProps {
  initialConversationId: string;
  initialMessage?: string;
}

const ChatInterface = forwardRef<ChatInterfaceRef, ChatInterfaceProps>(({ initialConversationId, initialMessage }, ref) => {
  const [inputValue, setInputValue] = useState('');
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  // Start loading if we have an initial conversation ID to load
  const [isLoadingConversation, setIsLoadingConversation] = useState(!!initialConversationId);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Drag and drop state
  const [isDragOver, setIsDragOver] = useState(false);
  
  // File upload state
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Track whether the initial message has been sent (for insurance report conversations)
  const initialMessageSentRef = useRef(false);
  
  // Attachment state - tracks uploaded attachments for this conversation
  const [attachments, setAttachments] = useState<Array<{
    id: string;
    filename: string;
    status: string;
    uploadedAt: Date;
    conversationId: string;
  }>>([]);

  // File upload handler
  const handleFileUpload = async (file: File) => {
    if (!file) return;

    setIsUploading(true);
    try {
      // Step 0: Resize image if needed for vision API compatibility
      const processedFile = await resizeImageForUpload(file);

      // Step 1: Create attachment record and get upload URL from GekkoVet API
      const attachment = await gekkoApi.createAttachment(processedFile.name, processedFile.type);
      
      // Add to local attachment tracking
      setAttachments(prev => [...prev, {
        id: attachment.id,
        filename: attachment.filename,
        status: attachment.status,
        uploadedAt: new Date(),
        conversationId: conversationId,
      }]);
      
      // Step 2: Upload file directly to S3 using pre-signed URL
      const uploadResponse = await fetch(attachment.upload_url, {
        method: 'PUT',
        body: processedFile,
        headers: {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          'Content-Type': processedFile.type,
        },
      });
      
      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }
      
      // Update status to uploaded in local state
      setAttachments(prev => prev.map(att => 
        att.id === attachment.id 
          ? { ...att, status: 'UPLOADED' }
          : att
      ));
      
      console.log('File uploaded successfully:', attachment);
      
    } catch (error) {
      console.error('File upload error:', error);
      // Remove failed attachment from state
      setAttachments(prev => prev.filter(att => att.filename !== file.name));
      // TODO: Show error toast to user
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const files = event.target.files;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (files && files.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      handleFileUpload(files[0]);
    }
  };

  const handleUploadClick = () => {
    const input = fileInputRef.current;
    if (input) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      input.click();
    }
  };

  // Modal state management
  const [diseaseModalOpen, setDiseaseModalOpen] = useState(false);
  const [selectedDiseaseId, setSelectedDiseaseId] = useState<number>(0);
  const [selectedDiseaseName, setSelectedDiseaseName] = useState<string>('');

  // Sources modal state (for multiple sources)
  const [sourcesModalOpen, setSourcesModalOpen] = useState(false);
  const [selectedSourceIds, setSelectedSourceIds] = useState<number[]>([]);

  // Conversation dashboard modal state
  const [dashboardModalOpen, setDashboardModalOpen] = useState(false);
  const [dashboardInitialTab, setDashboardInitialTab] = useState(0); // Default to Attachments tab (now index 0)
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<string | undefined>();

  // Document state
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | undefined>();

  // Case dialog state
  const [caseDialogOpen, setCaseDialogOpen] = useState(false);
  
  // Case state management
  const { caseState, setCaseId, updateCaseStatus, setProcessing, isEnabled } = useCaseState();

  const {
    messages,
    isConnected,
    isConnecting,
    error,
    conversationId,
    sendMessage,
    clearMessages,
    loadInitialMessages,
  } = useChat(initialConversationId);

  // Flash messages hook
  const { showAiDisclaimerMessage, showSuccess, showError } = useFlashMessages();

  // WebSocket for case event handling
  const { onMessage, getCurrentConversationId, setInsuranceReportId } = useWebSocket();

  // Handle case and document events from WebSocket
  useEffect(() => {
    const unsubscribe = onMessage((data: unknown) => {
      const typedData = data as {
        type: string;
        caseId?: string;
        conversationId?: string;
        status?: string;
        newStatus?: string;
        documentId?: string;
        version?: number;
        title?: string;
        templateName?: string;
        previousVersion?: number;
        revisionPrompt?: string;
      };

      // Only handle events for this conversation
      if (typedData.conversationId !== conversationId) {
        return;
      }

      if (typedData.type === 'case_created' && typedData.caseId) {
        console.log('Case created event received:', typedData.caseId);
        setCaseId(typedData.caseId);
        if (typedData.status) {
          updateCaseStatus(typedData.status);
        }
      } else if (typedData.type === 'case_status_changed' && typedData.caseId) {
        console.log('Case status changed event received:', typedData.caseId, typedData.newStatus);
        if (typedData.newStatus) {
          updateCaseStatus(typedData.newStatus);
        }
      } else if (typedData.type === 'document_created' && typedData.documentId) {
        console.log('Document created event received:', typedData.documentId);
        // Add new document to the list
        const newDoc: DocumentSummary = {
          documentId: typedData.documentId,
          version: typedData.version ?? 1,
          title: typedData.title ?? 'Untitled Document',
          templateName: typedData.templateName,
          status: (typedData.status as 'DRAFT' | 'FINAL' | 'ARCHIVED') ?? 'DRAFT',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          versionCount: 1,
        };
        setDocuments(prev => [newDoc, ...prev]);
      } else if (typedData.type === 'document_revised' && typedData.documentId) {
        console.log('Document revised event received:', typedData.documentId, 'v' + typedData.version);
        // Update document in the list with new version
        setDocuments(prev => prev.map(doc => {
          if (doc.documentId === typedData.documentId) {
            return {
              ...doc,
              version: typedData.version ?? doc.version + 1,
              updatedAt: new Date().toISOString(),
              versionCount: doc.versionCount + 1,
            };
          }
          return doc;
        }));
      }
    });

    return unsubscribe;
  }, [conversationId, setCaseId, updateCaseStatus, onMessage]);

  // Expose clearMessages function to parent
  useImperativeHandle(ref, () => ({
    clearMessages: handleClearChat,
  }));

  // Handle URN link clicks - memoized to prevent recreation during streaming
  const handleURNClick = useCallback((href: string, text: string) => {
    const parsedURN = parseURN(href);

    if (parsedURN.isValid && parsedURN.entityType === 'disease') {
      // Disease IDs are always numbers
      if (typeof parsedURN.entityId === 'number') {
        setSelectedDiseaseId(parsedURN.entityId);
        setSelectedDiseaseName(text);
        setDiseaseModalOpen(true);
      }
    } else if (parsedURN.isValid && parsedURN.entityType === 'source') {
      // Source IDs are always numbers
      if (typeof parsedURN.entityId === 'number') {
        setSelectedSourceIds([parsedURN.entityId]);
        setSourcesModalOpen(true);
      }
    } else if (parsedURN.isValid && parsedURN.entityType === 'case') {
      // Handle case URN clicks - open case dialog
      const caseId = parsedURN.entityId.toString();
      console.log('Case URN clicked:', caseId);
      setCaseId(caseId); // Set the case ID in case state
      setCaseDialogOpen(true); // Open dedicated case dialog
    } else if (parsedURN.isValid && parsedURN.entityType === 'document') {
      // Handle document URN clicks - open dashboard modal with Documents tab
      const documentId = parsedURN.entityId.toString();
      console.log('Document URN clicked:', documentId);
      setSelectedDocumentId(documentId);
      setDashboardInitialTab(1); // Documents tab is index 1
      setDashboardModalOpen(true);
    } else {
      console.warn('Unsupported URN or invalid format:', href);
    }
  }, []);

  // Dashboard modal handlers
  const handleOpenDashboard = (initialTab: number = 0, attachmentId?: string) => {
    setDashboardInitialTab(initialTab);
    setSelectedAttachmentId(attachmentId);
    setDashboardModalOpen(true);
  };

  const handleAttachmentClick = (attachmentId: string) => {
    handleOpenDashboard(0, attachmentId); // Open attachments tab with specific attachment
  };

  // Document loading callbacks for ConversationDashboardModal
  const handleLoadDocument = useCallback(async (documentId: string, version?: number): Promise<Document | null> => {
    try {
      return await copilotApi.getDocument(documentId, version);
    } catch (error) {
      console.error('Failed to load document:', error);
      return null;
    }
  }, []);

  const handleLoadDocumentVersions = useCallback(async (documentId: string): Promise<DocumentVersionInfo[]> => {
    try {
      return await copilotApi.getDocumentVersions(documentId);
    } catch (error) {
      console.error('Failed to load document versions:', error);
      return [];
    }
  }, []);

  // Case dialog handlers
  const handleOpenCaseDialog = () => {
    setCaseDialogOpen(true);
  };

  const handleCloseCaseDialog = () => {
    setCaseDialogOpen(false);
  };

  // Convert HTML element to formatted plain text for clipboard
  const htmlToPlainText = (element: HTMLElement): string => {
    const processNode = (node: Node, listType?: 'ul' | 'ol', listIndex?: number): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || '';
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
      }

      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();

      // Skip tool-call chips entirely or just get their text
      if (el.classList.contains('MuiChip-root')) {
        return '';
      }

      // Process children
      const processChildren = (newListType?: 'ul' | 'ol') => {
        let result = '';
        let itemIndex = 0;
        Array.from(el.childNodes).forEach(child => {
          if (newListType && (child as HTMLElement).tagName?.toLowerCase() === 'li') {
            itemIndex++;
            result += processNode(child, newListType, itemIndex);
          } else {
            result += processNode(child, newListType);
          }
        });
        return result;
      };

      switch (tag) {
        case 'br':
          return '\n';
        case 'p':
        case 'div':
          return processChildren() + '\n';
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          return '\n' + processChildren() + '\n';
        case 'ul':
          return processChildren('ul') + '\n';
        case 'ol':
          return processChildren('ol') + '\n';
        case 'li': {
          const prefix = listType === 'ol' ? `${listIndex}. ` : '• ';
          return prefix + processChildren().trim() + '\n';
        }
        case 'pre':
          return '\n' + (el.textContent || '') + '\n';
        case 'blockquote':
          return processChildren() + '\n';
        default:
          return processChildren();
      }
    };

    // Process and clean up excessive newlines
    return processNode(element)
      .replace(/\n{3,}/g, '\n\n')  // Max 2 consecutive newlines
      .trim();
  };

  // Copy message to clipboard as HTML
  const handleCopyMessage = async (message: ChatMessage) => {
    try {
      const messageElement = document.getElementById(`message-content-${message.id}`);
      if (!messageElement) {
        throw new Error('Message content element not found');
      }

      // Create a range and selection to copy the entire message content
      const range = document.createRange();
      range.selectNodeContents(messageElement);
      
      const selection = window.getSelection();
      if (!selection) {
        throw new Error('Could not create selection');
      }
      
      selection.removeAllRanges();
      selection.addRange(range);

      // Use the modern Clipboard API with both HTML and plain text
      if (navigator.clipboard && navigator.clipboard.write) {
        const htmlContent = messageElement.innerHTML;
        const textContent = htmlToPlainText(messageElement);
        
        // Create clipboard item with both HTML and plain text
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([textContent], { type: 'text/plain' })
        });
        
        await navigator.clipboard.write([clipboardItem]);
      } else {
        // Fallback to execCommand for older browsers
        document.execCommand('copy');
      }
      
      // Clear the selection
      selection.removeAllRanges();
      
      // Show flash message for successful copy
      showSuccess('Message copied to clipboard with rich formatting!');
    } catch (err) {
      console.error('Failed to copy message:', err);
      showError('Failed to copy message');
    }
  };





  // Preprocess markdown text to convert Sources shortcodes and tool markers into renderable elements
  // Memoized to avoid recreation on every render during streaming
  const preprocessMarkdown = useCallback((text: string): string => {
    // Convert [Sources:id1,id2,id3] or [Sources: id1,id2,id3] (with optional space) into [Sources](sources:id1,id2,id3)
    let processed = text.replace(/\[Sources:\s*([0-9,\s]+)\]/g, (_match, ids) => {
      // Remove any spaces from the IDs
      const cleanIds = ids.replace(/\s/g, '');
      return `[Sources](sources:${cleanIds})`;
    });

    // Convert [Web:id1,id2] inline web-source markers into [Web](web-sources:id1,id2)
    processed = processed.replace(/\[Web:\s*([0-9,\s]+)\]/g, (_match, ids) => {
      const cleanIds = ids.replace(/\s/g, '');
      return `[Web](web-sources:${cleanIds})`;
    });

    // Convert [[TOOL:name]] markers into a custom HTML element for rendering as chips
    // The tool-call element will be rendered by a custom override in markdownOptions
    // Escape underscores to prevent markdown from interpreting them as emphasis (e.g., _text_)
    processed = processed.replace(/\[\[TOOL:([^\]]+)\]\]/g, (_match, toolName) => {
      const escapedName = toolName.replace(/_/g, '\\_');
      return `<tool-call>${escapedName}</tool-call>`;
    });

    return processed;
  }, []);

  // Custom Link component for handling URN links and Sources shortcodes
  // Memoized to prevent recreation during streaming updates
  const URNAwareLink = useMemo(() => {
    const LinkComponent: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => {
      const parsedURN = parseURN(href);

      // Handle Sources links (href format: sources:id1,id2,id3)
      if (href.startsWith('sources:')) {
        const idsString = href.replace('sources:', '');
        const ids = idsString.split(',').map(id => parseInt(id.trim(), 10));
        const validIds = ids.filter(id => !isNaN(id) && id > 0);

        if (validIds.length > 0) {
          return <InlineSourceBadges sourceIds={validIds} />;
        }
      }

      // Handle Web sources (href format: web-sources:1,2)
      if (href.startsWith('web-sources:')) {
        const idsString = href.replace('web-sources:', '');
        const ids = idsString.split(',').map(id => parseInt(id.trim(), 10));
        const validIds = ids.filter(id => !isNaN(id) && id > 0);

        if (validIds.length > 0) {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const webSources = React.useContext(MessageWebSourcesContext);
          return <InlineWebSourceBadges webSourceIds={validIds} webSources={webSources} />;
        }
      }

      // Handle URN links
      if (parsedURN.isValid) {
        // Special handling for case URNs - show as a button
        if (parsedURN.entityType === 'case') {
          return (
            <Box
              component="button"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                border: 'none',
                borderRadius: 1,
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                handleURNClick(href, children as string);
              }}
              title={`Click to open case ${parsedURN.entityId}`}
            >
              📁 Open Case
            </Box>
          );
        }

        // Special handling for document URNs - show as a styled button
        if (parsedURN.entityType === 'document') {
          return (
            <Box
              component="button"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                backgroundColor: 'success.main',
                color: 'success.contrastText',
                border: 'none',
                borderRadius: 1,
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  backgroundColor: 'success.dark',
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                handleURNClick(href, children as string);
              }}
              title={`Click to view document`}
            >
              📄 {children}
            </Box>
          );
        }

        // Regular URN links (diseases, drugs, etc.)
        return (
          <Box
            component="span"
            sx={{
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': {
                color: 'primary.dark',
                textDecoration: 'none',
              }
            }}
            onClick={(e) => {
              e.preventDefault();
              handleURNClick(href, children as string);
            }}
            title={`Click for detailed information about ${children}`}
          >
            {children}
          </Box>
        );
      }

      // For non-URN links, render as normal external link
      return (
        <Box
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            '&:hover': {
              color: 'primary.dark',
            }
          }}
        >
          {children}
        </Box>
      );
    };
    return LinkComponent;
  }, [handleURNClick]);

  // Memoized markdown options to prevent recreation during streaming updates
  const markdownOptions = useMemo(() => ({
    overrides: {
      h1: {
        component: Typography,
        props: {
          variant: 'h6',
          sx: { mb: 1, fontWeight: 600 }
        }
      },
      h2: {
        component: Typography,
        props: {
          variant: 'subtitle1',
          sx: { mb: 0.5, fontWeight: 600 }
        }
      },
      h3: {
        component: Typography,
        props: {
          variant: 'subtitle2',
          sx: { mb: 0.5, fontWeight: 600 }
        }
      },
      p: {
        component: Typography,
        props: {
          variant: 'body1',
          sx: { mb: 0.5 }
        }
      },
      ul: {
        component: Box,
        props: {
          component: 'ul',
          sx: { mb: 1, pl: 2 }
        }
      },
      ol: {
        component: Box,
        props: {
          component: 'ol',
          sx: { mb: 1, pl: 2 }
        }
      },
      li: {
        component: Typography,
        props: {
          component: 'li',
          variant: 'body1',
          sx: { mb: 0.25 }
        }
      },
      strong: {
        component: Typography,
        props: {
          component: 'strong',
          sx: { fontWeight: 600 }
        }
      },
      code: {
        component: Typography,
        props: {
          component: 'code',
          sx: {
            bgcolor: 'grey.100',
            px: 0.5,
            py: 0.25,
            borderRadius: 1,
            fontSize: '0.875em',
            fontFamily: 'monospace'
          }
        }
      },
      pre: {
        component: Paper,
        props: {
          sx: {
            bgcolor: 'grey.50',
            p: 1.5,
            mt: 1,
            mb: 1,
            borderRadius: 1,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.875rem'
          }
        }
      },
      blockquote: {
        component: Box,
        props: {
          sx: {
            borderLeft: '3px solid',
            borderColor: 'grey.300',
            pl: 2,
            my: 1,
            fontStyle: 'italic'
          }
        }
      },
      // Custom component for rendering tool call markers as chips
      'tool-call': {
        component: ({ children }: { children: React.ReactNode }) => (
          <Chip
            icon={<ScheduleIcon fontSize="small" />}
            label={String(children)}
            size="small"
            sx={{
              my: 0.5,
              bgcolor: 'primary.50',
              color: 'primary.main',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              '& .MuiChip-icon': {
                color: 'primary.main',
              },
            }}
          />
        ),
      },
      a: {
        component: URNAwareLink
      }
    }
  }), [URNAwareLink]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start conversation when first message is sent
  useEffect(() => {
    if (messages.length > 0 && !hasStartedConversation) {
      setHasStartedConversation(true);
    }
  }, [messages.length, hasStartedConversation]);

  // Reset conversation state when conversation ID changes
  const previousConversationIdRef = useRef<string>(initialConversationId);
  useEffect(() => {
    if (conversationId !== previousConversationIdRef.current) {
      console.log(`Conversation ID changed from ${previousConversationIdRef.current} to ${conversationId}`);
      // Reset conversation state for new conversation
      setHasStartedConversation(false);
      setIsLoadingConversation(true); // Show loading indicator during switch
      // Clear attachments for new conversation
      setAttachments([]);
      // Reset initial message sent flag to allow auto-send for new conversation
      initialMessageSentRef.current = false;
      previousConversationIdRef.current = conversationId;
    }
  }, [conversationId]);

  // Always load from REST when conversation ID changes (optimized for 404 - new conversations)
  useEffect(() => {
    // Only load if we have messages cleared (indicating a conversation switch)
    if (messages.length === 0 && conversationId) {
      console.log(`Loading conversation from REST: ${conversationId}`);
      
      // Load conversation details via REST API
      copilotApi.getConversation(conversationId)
        .then(conversationDetails => {
          console.log(`Loaded ${conversationDetails.messages.length} messages for conversation ${conversationId}`);
          
          // Convert API messages to ChatMessage format
          const chatMessages: ChatMessage[] = conversationDetails.messages
            .filter(msg => msg.sender !== 'system') // Filter out system messages
            .map(msg => ({
              id: msg.messageId,
              text: msg.content,
              sender: msg.sender as 'user' | 'copilot',
              timestamp: new Date(msg.timestamp),
              conversationId: conversationId,
              sources: (msg.metadata?.sources || []).map(src => ({
                id: parseInt(src.id) || 0,
                name: src.name,
              })),
              webSources: msg.metadata?.webSources || [],
              entity_annotations: msg.metadata?.entityAnnotations?.map(annotation => ({
                entity_type: annotation.type as 'disease' | 'drug' | 'symptom' | 'procedure',
                entity_id: 0,
                entity_name: annotation.text,
                text_start: 0,
                text_end: 0,
                confidence: annotation.confidence,
              })) || [],
              attachmentIds: msg.metadata?.attachmentIds || [],
            }))
            .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()); // Sort messages chronologically

          loadInitialMessages(chatMessages);
          setHasStartedConversation(true);

          // Set case ID from conversation metadata if available
          if (conversationDetails.conversation.caseId) {
            console.log('Found case ID in conversation metadata:', conversationDetails.conversation.caseId);
            setCaseId(conversationDetails.conversation.caseId);
          }

          // Set insurance report ID from conversation metadata if available
          if (conversationDetails.conversation.insuranceReportId) {
            console.log('Found insurance report ID in conversation metadata:', conversationDetails.conversation.insuranceReportId);
            setInsuranceReportId(conversationDetails.conversation.insuranceReportId);
          } else {
            setInsuranceReportId(null);
          }

          // Load documents for this conversation
          copilotApi.getDocumentsForConversation(conversationId)
            .then(docs => {
              console.log(`Loaded ${docs.length} documents for conversation ${conversationId}`);
              setDocuments(docs);
            })
            .catch(err => {
              console.warn('Failed to load documents:', err);
            });

          setIsLoadingConversation(false); // Hide loading indicator
        })
        .catch(err => {
          // Optimize for 404 - new conversations don't have DB records yet and that's expected
          if (err instanceof CopilotApiError && err.statusCode === 404) {
            console.log(`Conversation ${conversationId} not found in DB (expected for new conversations)`);
            // For new conversations, just keep the conversation ready for first message
            setHasStartedConversation(false);
          } else {
            console.error('Failed to load existing conversation:', err);
          }
          setIsLoadingConversation(false); // Hide loading indicator in all cases
        });
    }
  }, [conversationId, messages.length, loadInitialMessages]);

  // Show AI usage disclaimer on component mount
  useEffect(() => {
    showAiDisclaimerMessage();
  }, [showAiDisclaimerMessage]);

  const scrollToBottom = () => {
    const element = messagesEndRef.current;
    if (element && 'scrollIntoView' in element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSendMessage = () => {
    if (inputValue.trim() && isConnected) {
      // Get attachment IDs for processed attachments
      const processedAttachments = attachments.filter(att => att.status === 'PROCESSED' || att.status === 'UPLOADED');
      const attachmentIds = processedAttachments.map(att => att.id);
      
      sendMessage(inputValue.trim(), attachmentIds.length > 0 ? attachmentIds : undefined);
      setInputValue('');
      
      setHasStartedConversation(true);
      
      // Clear attachments after sending
      setAttachments([]);
    }
  };

  // Auto-send initial message when coming from insurance results page
  useEffect(() => {
    // CRITICAL: Verify WebSocket is connected to the CORRECT conversation before sending
    // This prevents sending through an old connection when switching conversations
    const wsConversationId = getCurrentConversationId();
    const isCorrectConversation = wsConversationId === conversationId;

    if (initialMessage && isConnected && !isConnecting && !initialMessageSentRef.current && !hasStartedConversation && isCorrectConversation) {
      console.log('Auto-sending initial message for conversation:', conversationId);
      initialMessageSentRef.current = true;
      sendMessage(initialMessage.trim());
      setHasStartedConversation(true);
    } else if (initialMessage && !initialMessageSentRef.current && !hasStartedConversation && !isCorrectConversation) {
      console.log(`Waiting for correct WebSocket connection - current: ${wsConversationId}, needed: ${conversationId}`);
    }
  }, [initialMessage, isConnected, isConnecting, hasStartedConversation, sendMessage, conversationId, getCurrentConversationId]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = async () => {
    await clearMessages();
    setHasStartedConversation(false);
    // Clear attachments and documents when clearing chat
    setAttachments([]);
    setDocuments([]);
  };

  // Drag and drop event handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };



  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        minHeight: 'calc(100vh - 64px)', // 100vh minus AppBar height
        position: 'relative' 
      }}
    >

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ m: 2, mt: 8 }}>
          {error}
        </Alert>
      )}

      {/* Loading State - Show when switching conversations */}
      {isLoadingConversation && (
        <Fade in={isLoadingConversation} timeout={300}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderRadius: 2,
                bgcolor: 'background.paper',
              }}
            >
              <CircularProgress 
                size={40} 
                sx={{ 
                  mb: 2,
                  color: 'primary.main', // Use brand green color
                }} 
              />
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                Loading conversation...
              </Typography>
            </Paper>
          </Box>
        </Fade>
      )}

      {/* Empty State - ChatGPT-like centered layout */}
      {!hasStartedConversation && !isLoadingConversation && (
        <Fade in={!hasStartedConversation && !isLoadingConversation} timeout={300}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: { xs: 'flex-start', md: 'center' },
              px: { xs: 2, md: 3 },
              py: { xs: 2, md: 0 },
              zIndex: 1,
            }}
          >
            {/* Desktop Card */}
            <Card
              elevation={8}
              sx={{
                display: { xs: 'none', md: 'block' },
                p: 6,
                borderRadius: 4,
                bgcolor: 'background.paper',
                maxWidth: '900px',
                width: '100%',
                textAlign: 'center',
              }}
            >
              {/* Logo and Title */}
              <Box sx={{ mb: 6 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 3,
                    bgcolor: 'transparent',
                  }}
                >
                  <img 
                    src={new URL('../assets/gekko_eye.png', import.meta.url).href} 
                    alt="GekkoVet Copilot" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Avatar>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 300,
                    color: 'text.primary',
                    mb: 2,
                  }}
                >
                  GekkoVet Copilot
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'text.secondary',
                    fontWeight: 400,
                  }}
                >
                  How can I assist you with veterinary questions today?
                </Typography>
              </Box>

              {/* Input Field - Centered */}
              <Box sx={{ width: '100%', maxWidth: '1000px', mx: 'auto' }}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      placeholder={isDragOver ? "Drop files here to upload..." : (isConnected ? "Ask me anything about veterinary care..." : "Disconnected - Unable to send messages")}
                      variant="outlined"
                      size="medium"
                      error={!isConnected}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: isDragOver ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                          transition: 'background-color 0.2s ease-in-out',
                          '& fieldset': {
                            border: 'none',
                          },
                        },
                      }}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleUploadClick}
                      sx={{
                        bgcolor: 'grey.200',
                        color: 'text.secondary',
                        '&:hover': {
                          bgcolor: 'grey.300',
                        },
                      }}
                    >
                      <CloudUploadIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim() || !isConnected || isUploading}
                      sx={{
                        bgcolor: inputValue.trim() && isConnected && !isUploading ? 'primary.main' : 'grey.300',
                        color: 'white',
                        '&:hover': {
                          bgcolor: inputValue.trim() && isConnected && !isUploading ? 'primary.dark' : 'grey.400',
                        },
                        '&:disabled': {
                          bgcolor: 'grey.300',
                          color: 'grey.500',
                        },
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                  
                  {/* Upload indicator */}
                  {isUploading && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CloudUploadIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="body2" color="primary.main">
                        Uploading file...
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Attachment indicators */}
                  {attachments.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {attachments.map((attachment) => (
                        <Chip
                          key={attachment.id}
                          label={attachment.filename}
                          size="small"
                          icon={
                            attachment.status === 'PROCESSED' ? (
                              <CheckCircleIcon />
                            ) : attachment.status === 'ERROR' ? (
                              <ErrorIcon />
                            ) : (
                              <ScheduleIcon />
                            )
                          }
                          color={
                            attachment.status === 'PROCESSED' ? 'success' : 
                            attachment.status === 'ERROR' ? 'error' : 'default'
                          }
                          variant="outlined"
                          sx={{
                            '& .MuiChip-icon': {
                              fontSize: '16px',
                            },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  
                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    accept="image/*,application/pdf,.doc,.docx,.txt"
                  />
                </Paper>
              </Box>
            </Card>

            {/* Mobile Layout - No visible cards, bottom-aligned input */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexDirection: 'column',
                width: '100%',
                height: '100%',
              }}
            >
              {/* Mobile Logo and Title at top */}
              <Box sx={{ 
                textAlign: 'center',
                pt: 4,
                pb: 2,
                flex: '0 0 auto',
              }}>
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: 'transparent',
                  }}
                >
                  <img 
                    src={new URL('../assets/gekko_eye.png', import.meta.url).href} 
                    alt="GekkoVet Copilot" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Avatar>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 300,
                    color: 'text.primary',
                    mb: 1,
                  }}
                >
                  GekkoVet Copilot
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'text.secondary',
                    fontWeight: 400,
                  }}
                >
                  How can I assist you with veterinary questions today?
                </Typography>
              </Box>

              {/* Spacer to push input to bottom */}
              <Box sx={{ flex: '1 1 auto' }} />

              {/* Mobile Input - Bottom aligned, no paper wrapper */}
              <Box sx={{ pb: 2, flex: '0 0 auto' }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={5}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  placeholder={isDragOver ? "Drop files here to upload..." : (isConnected ? "Ask me anything about veterinary care..." : "Disconnected - Unable to send messages")}
                  variant="outlined"
                  size="medium"
                  error={!isConnected}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!inputValue.trim() || !isConnected || isUploading}
                        sx={{
                          bgcolor: !isUploading && inputValue.trim() && isConnected ? 'primary.main' : 'grey.300',
                          color: 'white',
                          mr: -1,
                          '&:hover': {
                            bgcolor: !isUploading && inputValue.trim() && isConnected ? 'primary.dark' : 'grey.400',
                          },
                          '&:disabled': {
                            bgcolor: 'grey.300',
                            color: 'grey.500',
                          },
                        }}
                      >
                        <SendIcon />
                      </IconButton>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: isDragOver ? 'rgba(76, 175, 80, 0.1)' : 'background.paper',
                      transition: 'background-color 0.2s ease-in-out',
                    },
                  }}
                />
                
                {/* Mobile File Upload Button - Left aligned below textarea */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                  <Button
                    variant="outlined"
                    onClick={handleUploadClick}
                    disabled={isUploading || !isConnected}
                    startIcon={<AttachFileIcon />}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                  >
                    {isUploading ? 'Uploading...' : 'Attach File'}
                  </Button>
                </Box>

                {/* Upload indicator */}
                {isUploading && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUploadIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2" color="primary.main">
                      Uploading file...
                    </Typography>
                  </Box>
                )}
                
                {/* Attachment indicators */}
                {attachments.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {attachments.map((attachment) => (
                      <Chip
                        key={attachment.id}
                        label={attachment.filename}
                        size="small"
                        icon={
                          attachment.status === 'PROCESSED' ? (
                            <CheckCircleIcon />
                          ) : attachment.status === 'ERROR' ? (
                            <ErrorIcon />
                          ) : (
                            <ScheduleIcon />
                          )
                        }
                        color={
                          attachment.status === 'PROCESSED' ? 'success' : 
                          attachment.status === 'ERROR' ? 'error' : 'default'
                        }
                        variant="outlined"
                        sx={{
                          '& .MuiChip-icon': {
                            fontSize: '16px',
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}

                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  accept="image/*,application/pdf,.doc,.docx,.txt"
                />
              </Box>
            </Box>
          </Box>
        </Fade>
      )}

      {/* Chat Messages and Input - Only show when conversation has started and not loading */}
      {hasStartedConversation && !isLoadingConversation && (
        <Fade in={hasStartedConversation && !isLoadingConversation} timeout={500}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100%',
            minHeight: 'calc(100vh - 64px)', // 100vh minus AppBar height
          }}>
            {/* Chat Messages */}
            <Box
              sx={{ 
                flexGrow: 1, 
                overflow: 'auto',
                // Dynamic bottom padding based on screen size and input height
                mb: { xs: '140px', md: '120px' }, // More space for mobile with buttons below textarea
              }}
            >
              <Container 
                maxWidth="lg" 
                sx={{ 
                  py: 2,
                }}
              >
                <Stack spacing={2}>
                  {messages.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                      mb: { xs: 0.5, md: 1 },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        maxWidth: { xs: '100%', sm: '90%', md: '85%' },
                        flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: message.sender === 'user' ? 'primary.main' : 'transparent',
                          mx: { xs: 0.5, md: 1 },
                        }}
                      >
                        {message.sender === 'user' ? (
                          <PersonIcon />
                        ) : (
                          <img 
                            src={message.isProgress 
                              ? new URL('../assets/gekko_eye_spinning.gif', import.meta.url).href
                              : new URL('../assets/gekko_eye.png', import.meta.url).href
                            } 
                            alt="GekkoVet Copilot" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        )}
                      </Avatar>
                      <Box sx={{ position: 'relative', width: '100%' }}>
                        <Card
                          sx={{
                            bgcolor: message.sender === 'user' 
                              ? 'primary.main' 
                              : (message.isThinking 
                                  ? 'rgb(230, 246, 244)' // Light brand-appropriate green background for thinking messages
                                  : 'background.paper'
                                ),
                            color: message.sender === 'user' ? 'primary.contrastText' : 'text.primary',
                            opacity: message.isProgress ? 0.8 : 1,
                            ...(message.isProgress && {
                              borderLeft: '3px solid',
                              borderColor: 'primary.main',
                            }),
                          }}
                        >
                        <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                          <Box id={`message-content-${message.id}`}>
                          {/* Top-right source badges (only when sources are present) */}
                          {message.sender === 'copilot' && !message.isThinking && !message.isProgress && (
                            <Box sx={{ mb: 1 }}>
                              <MessageSourceBadges
                                sources={message.sources || []}
                                webSources={message.webSources || []}
                              />
                            </Box>
                          )}
                          {message.sender === 'copilot' && !message.isThinking ? (
                            <MessageWebSourcesContext.Provider value={message.webSources || []}>
                              <Markdown options={markdownOptions}>
                                {preprocessMarkdown(message.text)}
                              </Markdown>
                            </MessageWebSourcesContext.Provider>
                          ) : (
                            <Typography
                              variant="body1"
                              sx={{
                                mb: 0.5,
                                fontStyle: message.isProgress ? 'italic' : 'normal',
                                ...(message.isThinking && {
                                  fontStyle: 'italic',
                                  fontFamily: 'serif', // More cursive-like appearance
                                }),
                              }}
                            >
                              {message.text}
                            </Typography>
                          )}
                          </Box>

                          {/* Show attachment indicator for user messages with attachments */}
                          {message.sender === 'user' && message.attachmentIds && message.attachmentIds.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, mb: 0.5 }}>
                              <Chip
                                size="small"
                                icon={<AttachFileIcon fontSize="small" />}
                                label={`${message.attachmentIds.length} attachment${message.attachmentIds.length > 1 ? 's' : ''}`}
                                variant="outlined"
                                clickable
                                onClick={() => message.attachmentIds && message.attachmentIds.length > 0 && 
                                  handleAttachmentClick(message.attachmentIds[0])}
                                sx={{
                                  fontSize: '0.7rem',
                                  height: '20px',
                                  color: message.sender === 'user' ? 'primary.contrastText' : 'text.secondary',
                                  borderColor: message.sender === 'user' ? 'primary.contrastText' : 'divider',
                                  '& .MuiChip-icon': {
                                    color: 'inherit',
                                  },
                                }}
                              />
                            </Box>
                          )}

                          {/* Timestamp and thought indicator row - on its own line within the card */}
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              mt: 1,
                              pt: 0.5,
                              borderTop: '1px solid',
                              borderColor: message.sender === 'user' ? 'rgba(255, 255, 255, 0.2)' : 'divider',
                            }}
                          >
                            {/* Timestamp and copy button - bottom left */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  opacity: 0.7,
                                  fontSize: '0.75rem',
                                  color: message.sender === 'user' ? 'inherit' : 'text.secondary',
                                }}
                              >
                                {message.timestamp.toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: undefined // This will use the browser's locale preference for 12/24 hour format
                                })}
                              </Typography>
                              {/* Only show copy button for copilot final answers (not thinking or progress messages) */}
                              {message.sender === 'copilot' && !message.isThinking && !message.isProgress && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyMessage(message)}
                                  sx={{
                                    opacity: 0.5,
                                    padding: 0.25,
                                    minWidth: 'auto',
                                    width: '16px',
                                    height: '16px',
                                    '&:hover': {
                                      opacity: 1,
                                      backgroundColor: 'action.hover',
                                    }
                                  }}
                                  title="Copy message to clipboard"
                                >
                                  <ContentCopyIcon sx={{ fontSize: '12px' }} />
                                </IconButton>
                              )}
                            </Box>


                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                    </Box>
                  </Box>
                ))}
                {/* Invisible div to scroll to */}
                <div ref={messagesEndRef} />
              </Stack>
            </Container>
          </Box>

            {/* Bottom Input - Fixed at bottom */}
            <Paper
              elevation={8}
              sx={{
                position: 'fixed',
                bottom: 0,
                left: { xs: 0, md: '280px' }, // Account for sidebar on desktop
                right: 0,
                zIndex: (theme) => theme.zIndex.appBar,
                borderRadius: 0,
                bgcolor: { xs: 'background.paper', md: 'transparent' },
                borderColor: 'divider',
                boxShadow: { xs: 'none', md: 'none' },
                borderTop: { xs: '1px solid', md: 'none' },
                borderTopColor: { xs: 'divider', md: 'transparent' },
              }}
            >
              <Container maxWidth="lg" sx={{ py: 2 }}>
                {/* Desktop Layout - Original horizontal layout */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={5}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    placeholder={isDragOver ? "Drop files here to upload..." : (isConnected ? "Ask me anything about veterinary care..." : "Disconnected - Unable to send messages")}
                    variant="outlined"
                    size="medium"
                    error={!isConnected}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        bgcolor: isDragOver ? 'rgba(76, 175, 80, 0.1)' : 'background.paper',
                        transition: 'background-color 0.2s ease-in-out',
                      },
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleUploadClick}
                    disabled={isUploading}
                    sx={{
                      mb: 1,
                      bgcolor: 'background.paper',
                      color: 'text.secondary',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        borderColor: 'grey.400',
                      },
                      '&:disabled': {
                        bgcolor: 'grey.100',
                        color: 'grey.400',
                        borderColor: 'grey.200',
                      },
                    }}
                  >
                    <CloudUploadIcon />
                  </IconButton>
                  <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || !isConnected || isUploading}
                    sx={{
                      mb: 1,
                      bgcolor: !isUploading && inputValue.trim() && isConnected ? 'primary.main' : 'grey.300',
                      color: 'white',
                      '&:hover': {
                        bgcolor: !isUploading && inputValue.trim() && isConnected ? 'primary.dark' : 'grey.400',
                      },
                      '&:disabled': {
                        bgcolor: 'grey.300',
                        color: 'grey.500',
                      },
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </Box>

                {/* Mobile Layout - Send button inside textarea, attach button below */}
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={5}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    placeholder={isDragOver ? "Drop files here to upload..." : (isConnected ? "Ask me anything about veterinary care..." : "Disconnected - Unable to send messages")}
                    variant="outlined"
                    size="medium"
                    error={!isConnected}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          color="primary"
                          onClick={handleSendMessage}
                          disabled={!inputValue.trim() || !isConnected || isUploading}
                          sx={{
                            bgcolor: !isUploading && inputValue.trim() && isConnected ? 'primary.main' : 'grey.300',
                            color: 'white',
                            mr: -1,
                            '&:hover': {
                              bgcolor: !isUploading && inputValue.trim() && isConnected ? 'primary.dark' : 'grey.400',
                            },
                            '&:disabled': {
                              bgcolor: 'grey.300',
                              color: 'grey.500',
                            },
                          }}
                        >
                          <SendIcon />
                        </IconButton>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        bgcolor: isDragOver ? 'rgba(76, 175, 80, 0.1)' : 'background.paper',
                        transition: 'background-color 0.2s ease-in-out',
                      },
                    }}
                  />
                  
                  {/* Mobile Buttons Row - Attach File left, Case and Dashboard right */}
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={handleUploadClick}
                      disabled={isUploading || !isConnected}
                      startIcon={<AttachFileIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                      }}
                    >
                      {isUploading ? 'Uploading...' : 'Attach File'}
                    </Button>
                    
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <CaseButton
                        enabled={isEnabled}
                        isProcessing={caseState.isProcessing}
                        onClick={handleOpenCaseDialog}
                        variant="mobile"
                        size="medium"
                      />
                      
                      <Badge badgeContent={attachments.length} color="secondary" max={99}>
                        <Button
                          variant="outlined"
                          size="medium"
                          onClick={() => handleOpenDashboard(0)}
                          startIcon={<DashboardIcon />}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            minWidth: 'auto',
                          }}
                        >
                          Dashboard
                        </Button>
                      </Badge>
                    </Box>
                  </Box>
                </Box>
                
                {/* Desktop Toolbar Section */}
                <Box sx={{ mt: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                  <CaseButton
                    enabled={isEnabled}
                    isProcessing={caseState.isProcessing}
                    onClick={handleOpenCaseDialog}
                    variant="desktop"
                    size="small"
                  />
                  
                  <Badge badgeContent={attachments.length} color="secondary" max={99}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleOpenDashboard(0)}
                      startIcon={<DashboardIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        minWidth: 'auto',
                      }}
                    >
                      Dashboard
                    </Button>
                  </Badge>
                </Box>

                {/* Shared Hidden File Input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  accept="image/*,.pdf,.doc,.docx,.txt"
                />
              </Container>
            </Paper>
          </Box>
        </Fade>
      )}
      
      {/* Disease Modal */}
      <DiseaseModal
        open={diseaseModalOpen}
        onClose={() => setDiseaseModalOpen(false)}
        diseaseId={selectedDiseaseId}
        diseaseName={selectedDiseaseName}
      />

      {/* Sources Modal */}
      <SourcesModal
        open={sourcesModalOpen}
        onClose={() => setSourcesModalOpen(false)}
        sourceIds={selectedSourceIds}
      />

      {/* Conversation Dashboard Modal */}
      <ConversationDashboardModal
        open={dashboardModalOpen}
        onClose={() => setDashboardModalOpen(false)}
        attachments={attachments.map(att => ({
          id: att.id,
          filename: att.filename,
          mime_type: 'application/octet-stream', // This should be provided by the API
          status: att.status as 'PENDING' | 'UPLOADED' | 'PROCESSED' | 'ERROR',
          created_at: att.uploadedAt.toISOString(),
          updated_at: att.uploadedAt.toISOString(),
        }))}
        documents={documents}
        initialTab={dashboardInitialTab}
        selectedAttachmentId={selectedAttachmentId}
        selectedDocumentId={selectedDocumentId}
        onLoadDocument={handleLoadDocument}
        onLoadDocumentVersions={handleLoadDocumentVersions}
      />

      {/* Case Dialog */}
      <CaseDialog
        open={caseDialogOpen}
        onClose={handleCloseCaseDialog}
        caseId={caseState.caseId || undefined}
      />

    </Box>
  );
});

ChatInterface.displayName = 'ChatInterface';

export default ChatInterface;